import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Phone, Book, Heart, FileText, Video, Users, ArrowRight, Warning } from '@phosphor-icons/react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const iconMap = {
  'phone': Phone,
  'book': Book,
  'heart': Heart,
  'file-text': FileText,
  'video': Video,
  'users': Users
};

const ResourceLibrary = () => {
  const [resources, setResources] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeCategory, setActiveCategory] = useState('all');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [resourcesRes, categoriesRes] = await Promise.all([
          axios.get(`${API}/resources`),
          axios.get(`${API}/resources/categories`)
        ]);
        setResources(resourcesRes.data);
        setCategories(categoriesRes.data);
      } catch (error) {
        console.error('Failed to fetch resources:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const filteredResources = activeCategory === 'all' 
    ? resources 
    : resources.filter(r => r.category === activeCategory);

  const crisisResources = resources.filter(r => r.category === 'crisis');

  return (
    <section id="resources" className="py-24 sm:py-32 bg-white" data-testid="resources-section">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-['Outfit'] font-medium text-[#2A2825] mb-4">
            Resource Library
          </h2>
          <p className="text-lg text-[#5C564F] max-w-2xl mx-auto">
            Curated resources to support your mental health journey. Remember, seeking help is a sign of strength.
          </p>
        </motion.div>

        {/* Crisis Banner */}
        {crisisResources.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-gradient-to-r from-[#D9534F] to-[#c9433f] rounded-3xl p-6 mb-12 text-white"
            data-testid="crisis-banner"
          >
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
                <Warning size={24} weight="fill" />
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-['Outfit'] font-semibold mb-2">In Crisis? Get Help Now</h3>
                <p className="text-white/90 mb-4">If you're in immediate danger or having thoughts of self-harm, please reach out:</p>
                <div className="flex flex-wrap gap-3">
                  {crisisResources.map((resource, index) => (
                    <a
                      key={index}
                      href={resource.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="bg-white text-[#D9534F] px-4 py-2 rounded-full text-sm font-medium hover:bg-white/90 transition-colors"
                      data-testid={`crisis-resource-${index}`}
                    >
                      {resource.title}
                    </a>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Category Tabs */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="flex flex-wrap gap-2 justify-center mb-12"
        >
          <button
            onClick={() => setActiveCategory('all')}
            className={`px-6 py-3 rounded-full text-sm font-medium transition-all duration-300 ${
              activeCategory === 'all'
                ? 'bg-[#B46C4A] text-white'
                : 'bg-[#F2EBE1] text-[#5C564F] hover:bg-[#E5DCD0]'
            }`}
            data-testid="category-all"
          >
            All Resources
          </button>
          {categories.filter(c => c.id !== 'crisis').map((category) => {
            const Icon = iconMap[category.icon] || Book;
            return (
              <button
                key={category.id}
                onClick={() => setActiveCategory(category.id)}
                className={`px-6 py-3 rounded-full text-sm font-medium transition-all duration-300 flex items-center gap-2 ${
                  activeCategory === category.id
                    ? 'bg-[#B46C4A] text-white'
                    : 'bg-[#F2EBE1] text-[#5C564F] hover:bg-[#E5DCD0]'
                }`}
                data-testid={`category-${category.id}`}
              >
                <Icon size={18} weight={activeCategory === category.id ? 'fill' : 'regular'} />
                {category.name}
              </button>
            );
          })}
        </motion.div>

        {/* Resources Grid */}
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="loader"></div>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="resources-grid">
            {filteredResources.filter(r => r.category !== 'crisis').map((resource, index) => {
              const category = categories.find(c => c.id === resource.category);
              const Icon = category ? iconMap[category.icon] || Book : Book;
              
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-[#F9F6F0] rounded-3xl p-6 border border-[#E5DCD0] hover:shadow-lg hover:-translate-y-1 transition-all duration-300 group"
                  data-testid={`resource-card-${index}`}
                >
                  <div className="flex items-start gap-4 mb-4">
                    <div className="w-12 h-12 bg-[#B46C4A]/10 rounded-2xl flex items-center justify-center flex-shrink-0 group-hover:bg-[#B46C4A]/20 transition-colors">
                      <Icon size={24} className="text-[#B46C4A]" weight="fill" />
                    </div>
                    <div className="flex-1">
                      <span className="text-xs font-medium text-[#647B64] uppercase tracking-wider">
                        {category?.name || resource.category}
                      </span>
                      <h3 className="text-lg font-['Outfit'] font-medium text-[#2A2825] mt-1">
                        {resource.title}
                      </h3>
                    </div>
                  </div>
                  
                  <p className="text-[#5C564F] text-sm leading-relaxed mb-4">
                    {resource.description}
                  </p>

                  {resource.content && (
                    <div className="bg-white rounded-2xl p-4 text-sm text-[#5C564F] mb-4 border border-[#E5DCD0]">
                      {resource.content}
                    </div>
                  )}

                  {resource.link && (
                    <a
                      href={resource.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-[#B46C4A] hover:text-[#9B5C3E] font-medium text-sm group-hover:gap-3 transition-all"
                    >
                      Learn more <ArrowRight size={16} weight="bold" />
                    </a>
                  )}
                </motion.div>
              );
            })}
          </div>
        )}

        {filteredResources.filter(r => r.category !== 'crisis').length === 0 && !loading && (
          <div className="text-center py-12 text-[#5C564F]">
            No resources found in this category yet.
          </div>
        )}
      </div>
    </section>
  );
};

export default ResourceLibrary;
